<template>
  <admin-navbar>
    <div class="bg-white rounded shadow">
      <h6
        class="text-base font-bold border-t-4 border-t-blue-500 rounded-t border-b p-3"
      >
        Өөрийн үнэлгээнд хариулсан гишүүн байгууллагууд
      </h6>

      <div v-if="!isLoading && respondents.length > 0" class="p-3 space-y-5">
        <div class="flex justify-between items-center text-xs">
          <div class="space-x-3">
            <span>Хуудас бүрт:</span>
            <select
              name="dataPerPage"
              id="dataPerPage"
              class="border rounded p-1 text-blue-500"
              v-model="dataPerPage"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
            </select>
          </div>

          <span>Нийт үнэлгээ: {{ totalRespondentCount }} </span>

          <div class="flex items-center gap-3">
            <span>Нийт хуудас: {{ totalPages }} </span>
            <PaginationCart
              :totalPages="totalPages"
              :modelValue="currentPage"
              :style="{
                color: 'blue-500',
                height: 7,
                width: 7,
                fontSize: 'xs',
              }"
              @page-changed="onPageChanged"
            />
          </div>
        </div>

        <hot-table :settings="hotSettings" :data="respondents"></hot-table>
      </div>

      <div
        v-else-if="!isLoading && respondents.length === 0"
        class="flex justify-center items-center h-[80vh]"
      >
        Жагсаалт хоосон байна
      </div>

      <div v-else class="flex justify-center items-center h-[80vh]">
        <spin-loading :color="'rgb(59 130 246)'" />
      </div>
    </div>
  </admin-navbar>

  <MMAdminMemberAnswer
    v-if="isAnswers"
    @closeAnswers="closeAnswers"
    :memberId="memberId"
    :answerId="answerId"
  />

  <MMAdminMemberFiles
    v-if="isFiles"
    @closeAnswers="closeFiles"
    :memberId="memberId"
    :answerId="answerId"
  />
</template>

<script>
import AdminNavbar from "@/components/admin/AdminNavbar.vue";
import SpinLoading from "@/components/ui/SpinLoading.vue";
import adminInstance from "@/lib/adminInstance";
import { onMounted, ref, watch } from "vue";
import { HotTable } from "@handsontable/vue3";
import { registerAllModules } from "handsontable/registry";
import "handsontable/dist/handsontable.full.min.css";
import "handsontable/dist/handsontable.full.css";
import MMAdminMemberAnswer from "@/containers/MiningMongolia/admin/MMAdminMemberAnswer.vue";
import MMAdminMemberFiles from "@/containers/MiningMongolia/admin/MMAdminMemberFiles.vue";
import PaginationCart from "@/components/ui/PaginationCart.vue";

registerAllModules();

export default {
  name: "MMMemberAssessmentResult",
  components: {
    AdminNavbar,
    SpinLoading,
    HotTable,
    MMAdminMemberAnswer,
    MMAdminMemberFiles,
    PaginationCart,
  },
  setup() {
    const isLoading = ref(false);
    const respondents = ref([]);
    const dataPerPage = ref(10);
    const totalRespondentCount = ref(null);
    const totalPages = ref(1);
    const currentPage = ref(1);

    const fetchMembersAssessmentResults = async () => {
      try {
        isLoading.value = true;
        const res = await adminInstance.get(
          `/get/member-assessment-result?page=${currentPage.value}&limit=${dataPerPage.value}`
        );
        respondents.value = res.data.respondents;
        totalPages.value = res.data.totalPages;
        totalRespondentCount.value = res.data.total;
      } catch (err) {
        return err;
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(async () => {
      await fetchMembersAssessmentResults();
    });

    watch([() => dataPerPage.value], () => {
      currentPage.value = 1;
      fetchMembersAssessmentResults();
    });

    const onPageChanged = (page) => {
      currentPage.value = page;
      fetchMembersAssessmentResults();
    };

    const commonRenderer = (instance, td, row, col, prop, value) => {
      td.classList.add("htMiddle");
      td.innerHTML = `<span class="flex justify-center items-center p-2 text-black">${
        value ? value : ""
      }</span>`;
      return td;
    };

    const hotSettings = {
      colHeaders: [
        "Байгууллагын нэр",
        "И-мэйл хаяг",
        "Үнэлгээнд оролцсон огноо",
        "Нийтлэх",
        "Хариулт",
        "Файлууд татах",
      ],
      columns: [
        {
          data: "name",
          readOnly: true,
          renderer: commonRenderer,
        },
        {
          data: "email",
          readOnly: true,
          renderer: commonRenderer,
        },
        {
          data: "created_at",
          readOnly: true,
          renderer: commonRenderer,
        },
        {
          data: "publication",
          readOnly: true,
          renderer: function (instance, td, row, col, prop, value) {
            td.classList.add("htMiddle");
            td.innerHTML = `<span class="flex justify-center items-center p-2 text-black">${
              value === true ? "Зөвшөөрсөн" : "Зөвшөөрөөгүй"
            }</span>`;
            return td;
          },
        },
        {
          readOnly: true,
          renderer: function (inst, td, row) {
            const respondent = respondents.value[row];
            td.classList.add("htCenter", "htMiddle", "htButton");
            td.innerHTML = `<div class="flex justify-center items-center p-2 gap-2 text-white">
                  <button class="flex items-center bg-sky-500 py-1 px-2 rounded edit-btn">
                      <svg class="fill-current w-3 h-3 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M0 448c0 35.3 28.7 64 64 64l160 0 0-128c0-17.7 14.3-32 32-32l128 0 0-288c0-35.3-28.7-64-64-64L64 0C28.7 0 0 28.7 0 64L0 448zM171.3 75.3l-96 96c-6.2 6.2-16.4 6.2-22.6 0s-6.2-16.4 0-22.6l96-96c6.2-6.2 16.4-6.2 22.6 0s6.2 16.4 0 22.6zm96 32l-160 160c-6.2 6.2-16.4 6.2-22.6 0s-6.2-16.4 0-22.6l160-160c6.2-6.2 16.4-6.2 22.6 0s6.2 16.4 0 22.6zM384 384l-128 0 0 128L384 384z"/></svg>
                      <span>Хариулт харах</span>
                  </button>
              </div>`;

            const editButton = td.querySelector(".edit-btn");
            if (editButton) {
              editButton.addEventListener("click", () => {
                showAnswers(respondent);
              });
            }
          },
        },
        {
          readOnly: true,
          renderer: function (inst, td, row) {
            const respondent = respondents.value[row];
            td.classList.add("htCenter", "htMiddle", "htButton");
            td.innerHTML = `<div class="flex justify-center items-center p-2 gap-2 text-white">
                  <button class="flex items-center bg-amber-500 py-1 px-2 rounded edit-btn">
                      <svg class="fill-current w-3 h-3 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M0 448c0 35.3 28.7 64 64 64l160 0 0-128c0-17.7 14.3-32 32-32l128 0 0-288c0-35.3-28.7-64-64-64L64 0C28.7 0 0 28.7 0 64L0 448zM171.3 75.3l-96 96c-6.2 6.2-16.4 6.2-22.6 0s-6.2-16.4 0-22.6l96-96c6.2-6.2 16.4-6.2 22.6 0s6.2 16.4 0 22.6zm96 32l-160 160c-6.2 6.2-16.4 6.2-22.6 0s-6.2-16.4 0-22.6l160-160c6.2-6.2 16.4-6.2 22.6 0s6.2 16.4 0 22.6zM384 384l-128 0 0 128L384 384z"/></svg>
                      <span>Файлууд татах</span>
                  </button>
              </div>`;

            const editButton = td.querySelector(".edit-btn");
            if (editButton) {
              editButton.addEventListener("click", () => {
                showFiles(respondent);
              });
            }
          },
        },
      ],
      rowHeaders: true,
      width: "100%",
      height: "70vh",
      fixedColumnsLeft: 1,
      contextMenu: true,
      manualColumnFreeze: true,
      manualColumnMove: true,
      manualColumnResize: true,
      manualRowResize: true,
      manualRowMove: true,
      dropdownMenu: true,
      filters: true,
      columnSorting: true,
      licenseKey: "non-commercial-and-evaluation",
    };

    const isAnswers = ref(false);
    const answerId = ref(null);
    const memberId = ref(null);

    const showAnswers = (member) => {
      answerId.value = member.answer_id;
      memberId.value = member.member_id;
      isAnswers.value = !isAnswers.value;
    };

    const closeAnswers = () => {
      isAnswers.value = !isAnswers.value;
    };

    const isFiles = ref(false);
    const showFiles = (member) => {
      answerId.value = member.answer_id;
      memberId.value = member.member_id;
      isFiles.value = !isFiles.value;
    };

    const closeFiles = () => {
      isFiles.value = !isFiles.value;
    };

    return {
      isLoading,
      respondents,
      currentPage,
      dataPerPage,
      totalPages,
      totalRespondentCount,
      hotSettings,
      onPageChanged,
      isAnswers,
      showAnswers,
      closeAnswers,
      answerId,
      memberId,
      isFiles,
      showFiles,
      closeFiles,
    };
  },
};
</script>
